"use client";

import { useState, useEffect } from "react";
import { Building, Mail, Globe, Briefcase, Save, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackButton from "../shared/backButton";
import Modal from "../layout/Modal";
import CustomTooltip from "../shared/CustomTooltip";
import "./OrganizationCreatePage.css";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import {
  onboardOrganizationApi,
  getIndustriesListApi,
} from "../../services/settingsService";
import MessageManager from "../layout/Message";

const OrganizationCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    organizationId: null,
  });
  const [formData, setFormData] = useState({
    organizationName: "",
    email: "",
    website: "",
    industry: "",
    adminName: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [industries, setIndustries] = useState([]);

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Mandatory fields
    if (!formData.organizationName.trim()) {
      newErrors.organizationName = t(
        "organizationCreate.errors.organizationNameRequired",
      );
    }
    if (!formData.email.trim()) {
      newErrors.email = t("organizationCreate.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("organizationCreate.errors.emailInvalid");
    }
    if (!formData.adminName.trim()) {
      newErrors.adminName = t("organizationCreate.errors.adminNameRequired");
    }

    // if (!formData.website.trim()) {
    //   newErrors.website = t("organizationCreate.errors.websiteRequired");
    // } else if (!/^https?:\/\/.+\..+/.test(formData.website)) {
    //   newErrors.website = t("organizationCreate.errors.websiteInvalid");
    // }
    if (!formData.website.trim()) {
      newErrors.website = t("organizationCreate.errors.websiteRequired");
    } else {
      const websiteValue = formData.website.trim();
      const normalizedWebsite = /^https?:\/\//i.test(websiteValue)
        ? websiteValue
        : `https://${websiteValue}`;

      try {
        const url = new URL(normalizedWebsite);
        const hostname = url.hostname;
        const labels = hostname.split(".");

        const isValidWebsite =
          ["http:", "https:"].includes(url.protocol) &&
          !/\s/.test(websiteValue) &&
          hostname.includes(".") &&
          !hostname.includes("..") &&
          labels.every(
            (label) =>
              label.length >= 1 &&
              label.length <= 63 &&
              /^[a-zA-Z0-9-]+$/.test(label) &&
              !label.startsWith("-") &&
              !label.endsWith("-"),
          ) &&
          /^[a-zA-Z]{2,}$/.test(labels[labels.length - 1]);

        if (!isValidWebsite) {
          newErrors.website = t("organizationCreate.errors.websiteInvalid");
        }
      } catch {
        newErrors.website = t("organizationCreate.errors.websiteInvalid");
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting form with data:", formData);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const obj = {
        organization: formData.organizationName,
        superAdminEmail: formData.email,
        website: formData.website,
        superAdminName: formData.adminName,
        industry: formData.industry || "",
      };
      const response = await onboardOrganizationApi(obj);
      console.log("Organization created successfully:", response);
      if (response.code == 200) {
        MessageManager.showToast(
          "success",
          t("organizationCreate.organizationCreatedMessage", {
            organizationName: formData.organizationName,
          }),
        );
        setTimeout(() => {
          navigate("/brand");
        }, 1000);
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        title: t("organizationCreate.errorCreatingOrganization"),
        message: t("organizationCreate.errorCreatingOrganizationMessage"),
        showButton: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    getIndustriesListApiHandler();
  }, []);

  const getIndustriesListApiHandler = async () => {
    try {
      const response = await getIndustriesListApi();
      setIndustries(response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const handleCancel = () => {
    navigate("/brand");
  };

  return (
    <div className="org-create-page">
      <main className="org-create-main">
        <div className="container">
          <div className="org-create-header">
            <BackButton />

            <div className="org-create-title">
              <div className="org-create-icon">
                <Building size={32} />
              </div>
              <h1>{t("organizationCreate.pageTitle")}</h1>
              <p>{t("organizationCreate.pageDescription")}</p>
            </div>
          </div>

          <div className="org-create-form-container">
            <form onSubmit={handleSubmit} className="org-create-form">
              <div className="org-form-section">
                <h2>{t("organizationCreate.organizationInformation")}</h2>
                <p className="org-section-description">
                  {t("organizationCreate.basicInformation")}
                </p>

                <div className="org-form-grid">
                  <div className="org-form-group ">
                    <div className="org-brand-label-row">
                      <label
                        htmlFor="organizationName"
                        className="org-form-label org-required"
                      >
                        <Building size={16} />
                        {t("organizationInfo.BrandName")}
                      </label>
                      <CustomTooltip
                        tooltip={{
                          message: t(
                            "organizationCreate.brandNameChangeLockedTooltip",
                          ),
                        }}
                        id="organization-name-setup-tooltip"
                        className="org-brand-name-tooltip"
                      >
                        <Info
                          size={14}
                          color="#888"
                          style={{
                            cursor: "pointer",
                            verticalAlign: "middle",
                          }}
                        />
                      </CustomTooltip>
                    </div>
                    <input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.organizationName ? "org-error" : ""
                      }`}
                      placeholder={t(
                        "organizationCreate.enterOrganizationName",
                      )}
                    />
                    {errors.organizationName && (
                      <span className="org-error-message">
                        {errors.organizationName}
                      </span>
                    )}
                  </div>
                  <div className="org-form-group ">
                    <div className="org-brand-label-row">
                      <label
                        htmlFor="adminName"
                        className="org-form-label org-required"
                      >
                        <Building size={16} />
                        {t("organizationCreate.adminName")}
                      </label>
                      <CustomTooltip
                        tooltip={{
                          message: t(
                            "organizationCreate.adminNameChangeLockedTooltip",
                          ),
                        }}
                        id="organization-name-setup-tooltip"
                        className="org-brand-name-tooltip"
                      >
                        <Info
                          size={14}
                          color="#888"
                          style={{
                            cursor: "pointer",
                            verticalAlign: "middle",
                          }}
                        />
                      </CustomTooltip>
                    </div>

                    <input
                      type="text"
                      id="adminName"
                      name="adminName"
                      value={formData.adminName}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.adminName ? "org-error" : ""
                      }`}
                      placeholder={t("organizationCreate.enterAdminName")}
                    />
                    {errors.adminName && (
                      <span className="org-error-message">
                        {errors.adminName}
                      </span>
                    )}
                  </div>

                  <div className="org-form-group">
                    <label
                      htmlFor="email"
                      className="org-form-label org-required"
                    >
                      <Mail size={16} />
                      {t("organizationCreate.emailAddress")}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.email ? "org-error" : ""
                      }`}
                      placeholder={t("organizationCreate.emailPlaceholder")}
                    />
                    {errors.email && (
                      <span className="org-error-message">{errors.email}</span>
                    )}
                  </div>

                  <div className="org-form-group">
                    <label
                      htmlFor="website"
                      className="org-form-label org-required"
                    >
                      <Globe size={16} />
                      {t("organizationInfo.webAddress")}
                    </label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className={`org-form-input ${
                        errors.website ? "org-error" : ""
                      }`}
                      placeholder={t("organizationCreate.websitePlaceholder")}
                    />
                    {errors.website && (
                      <span className="org-error-message">
                        {errors.website}
                      </span>
                    )}
                  </div>

                  <div className="org-form-group">
                    <label
                      htmlFor="industry"
                      className="org-form-label org-optional"
                    >
                      <Briefcase size={16} />
                      {t("organizationInfo.industry")}
                      <span className="org-optional-text">
                        ({t("common.optional")})
                      </span>
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      className="org-form-select"
                    >
                      <option value="">
                        {t("organizationCreate.selectIndustry")}
                      </option>
                      {industries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="org-form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="org-btn org-btn-outline"
                  disabled={isSubmitting}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="org-btn org-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="org-spinner" />
                      {t("organizationCreate.creating")}
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {t("organizationCreate.createOrganization")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        <Modal
          show={showModal.show}
          onClose={() =>
            setShowModal({
              show: false,
              type: "",
              title: "",
              message: "",
              action: null,
            })
          }
          type={showModal.type}
          title={showModal.title}
          message={showModal.message}
          showButton={false}
        ></Modal>
      </main>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default OrganizationCreatePage;
